/**
 * # dxfBridge.newRecordHandles.layouts — LAYOUT tabs created in an imported drawing
 *
 * Section 4 of the imported-document export preparation
 * (`dxfBridge.newRecordHandles.ts`). `LAYOUT NEW` (`shell/add-space`) adds a
 * layout SPACE plus a `buildDefaultLayout` record with `handle: ""` and
 * `ownerBlockRecordHandle: ""` — no BLOCK_RECORD, no BLOCK, no dictionary
 * child — so the exported LAYOUT was handle-less (silently minted by ezdxf),
 * unowned, absent from the ACAD_LAYOUT dictionary and its tab's entities had
 * no owning record. AutoCAD's structure for a paper tab (Autodesk DXF
 * reference LAYOUT / BLOCK_RECORD; ezdxf `Layouts.new`) is:
 *
 *   LAYOUT `5 <h>` / `330 <ACAD_LAYOUT dictionary>` / `330 (AcDbLayout) <BLOCK_RECORD>`
 *   BLOCK_RECORD `*Paper_Space<N>` `340 <LAYOUT h>`  +  an (empty) `*Paper_Space<N>` BLOCK
 *   DICTIONARY ACAD_LAYOUT child `3 <tab name>` / `350 <LAYOUT h>`
 *
 * This mirrors what the conformant skeleton's paper-space reconciliation does
 * for a from-scratch document (`reconcilePaperSpaces` + the shared
 * `patchLayoutDictionaryEntries`), applied only to a tab CREATED after import
 * — its LAYOUT object exists with an empty handle and its LAYOUT ↔
 * BLOCK_RECORD chain does not resolve (`resolvePaperBlockName`, the same
 * resolver the export's paper-space plan uses). An imported tab is untouched
 * whether its chain resolves or not (nothing is invented for a partial source). The un-suffixed `*Paper_Space` record is reused for
 * the FIRST paper tab when no other layout owns it (the AutoCAD "Layout1"
 * shape); further tabs mint `*Paper_Space<N>` with the smallest free N.
 */
import { patchLayoutDictionaryEntries } from "./dxfBridge.conformantSkeleton";
import { findRootDictionaryEntryHandle } from "./dxfBridge.conformantSkeleton.tableOwners";
import {
  lacksOwnHandle,
  mintBlockRecord,
  type PreparationContext,
} from "./dxfBridge.newRecordHandles.context";
import {
  isFirstPaperSpaceBlockName,
  resolvePaperBlockName,
} from "./dxfBridge.paperSpaceLayout";
import {
  blockDefinitionSchema,
  blockRecordSchema,
  type BlockDefinition,
  type BlockRecord,
  type DrawingDocument,
  type Layout,
} from "./model";

const MODEL_SPACE_BLOCK_RECORD = "*Model_Space";
const PAPER_SPACE_BLOCK_RECORD = "*Paper_Space";

/** Result of {@link prepareImportedLayouts} — same references when unchanged. */
export interface PreparedLayouts {
  blocks: DrawingDocument["blocks"];
  blockRecords: DrawingDocument["blockRecords"];
  layouts: DrawingDocument["layouts"];
  layoutsBySpaceId: DrawingDocument["layoutsBySpaceId"];
  rawObjectGroups: DrawingDocument["rawObjectGroups"];
  /** Space id → the BLOCK_RECORD handle its entities are owned by (330). */
  ownerBySpaceId: Map<string, string | undefined>;
}

function findRecordName(records: Record<string, BlockRecord>, name: string): string | undefined {
  const lower = name.toLowerCase();
  return Object.keys(records).find((k) => k.toLowerCase() === lower);
}

/** Smallest free `*Paper_Space<N>` (N ≥ 0) among the existing record / block names. */
function nextPaperSpaceName(
  records: Record<string, BlockRecord>,
  blocks: Record<string, BlockDefinition>,
): string {
  const taken = new Set([...Object.keys(records), ...Object.keys(blocks)].map((n) => n.toLowerCase()));
  let n = 0;
  while (taken.has(`${PAPER_SPACE_BLOCK_RECORD}${n}`.toLowerCase())) n += 1;
  return `${PAPER_SPACE_BLOCK_RECORD}${n}`;
}

/** The ACAD_LAYOUT dictionary handle: a layout's owner, else the root dictionary entry. */
function findLayoutDictionaryHandle(document: DrawingDocument): string | undefined {
  for (const layout of Object.values(document.layouts ?? {})) {
    const h = layout.dictionaryOwnerHandle?.trim();
    if (h && !lacksOwnHandle(h)) return h;
  }
  return findRootDictionaryEntryHandle(document.rawObjectGroups, "ACAD_LAYOUT");
}

export function prepareImportedLayouts(
  document: DrawingDocument,
  ctx: PreparationContext,
): PreparedLayouts {
  const blocks: Record<string, BlockDefinition> = { ...document.blocks };
  const blockRecords: Record<string, BlockRecord> = { ...document.blockRecords };
  const layouts: Record<string, Layout> = { ...(document.layouts ?? {}) };
  const layoutsBySpaceId: Record<string, Layout> = { ...(document.layoutsBySpaceId ?? {}) };
  const ownerBySpaceId = new Map<string, string | undefined>();
  const dictHandle = findLayoutDictionaryHandle(document);
  let changed = false;

  // Existing LAYOUT objects claim their handles (a non-hex one is minted below
  // when its space is visited; a detached one right here).
  for (const [name, layout] of Object.entries(layouts)) {
    if (!lacksOwnHandle(layout.handle)) ctx.claim(layout.handle);
    else if (!document.spaces.some((s) => s.kind === "layout" && s.name === name)) {
      layouts[name] = { ...layout, handle: ctx.mint() };
      changed = true;
    }
  }

  const modelRecordName = findRecordName(blockRecords, MODEL_SPACE_BLOCK_RECORD);
  // A tab can be bound only inside a STRUCTURALLY COMPLETE imported drawing
  // (an ACAD_LAYOUT dictionary + a hex `*Model_Space` record — every R13+
  // drawing has both). Such a drawing carries LAYOUT objects for all its tabs,
  // so an unresolved layout with an EMPTY handle there can only be LAYOUT NEW.
  // A partial / hand-built source (no dictionary, handle-less records) makes
  // the importer synthesize a default layout itself (`dxfBridge.spaces.ts`),
  // which must stay pass-through — nothing is invented for it.
  const structurallyComplete =
    dictHandle !== undefined &&
    modelRecordName !== undefined &&
    !lacksOwnHandle(blockRecords[modelRecordName]!.handle);
  const boundRecordHandles = new Set<string>();
  for (const layout of Object.values(layouts)) {
    const h = layout.ownerBlockRecordHandle?.trim().toLowerCase();
    if (h) boundRecordHandles.add(h);
  }

  for (const space of document.spaces) {
    if (space.kind === "model") {
      ownerBySpaceId.set(space.id, modelRecordName ? blockRecords[modelRecordName]!.handle : undefined);
      continue;
    }
    if (space.kind !== "layout") {
      ownerBySpaceId.set(space.id, undefined);
      continue;
    }
    const resolved = resolvePaperBlockName(
      { ...document, blockRecords, layouts, layoutsBySpaceId },
      space,
    );
    if (resolved !== undefined) {
      ownerBySpaceId.set(space.id, blockRecords[resolved]?.handle);
      continue;
    }
    // Only a tab CREATED after import (`shell/add-space`: its LAYOUT object
    // exists with an EMPTY handle) is bound here. An imported tab whose chain
    // merely does not resolve (a hand-built / partial source) keeps its
    // pass-through shape — nothing is invented for it.
    const source = layoutsBySpaceId[space.id] ?? layouts[space.name];
    if (!structurallyComplete || source === undefined || !lacksOwnHandle(source.handle)) {
      ownerBySpaceId.set(space.id, undefined);
      continue;
    }
    // Bind the new tab to a BLOCK_RECORD + BLOCK.
    let recordName = findRecordName(blockRecords, PAPER_SPACE_BLOCK_RECORD);
    const firstFree =
      recordName !== undefined &&
      isFirstPaperSpaceBlockName(recordName) &&
      !boundRecordHandles.has((blockRecords[recordName]!.handle ?? "").toLowerCase());
    if (!firstFree) {
      recordName = nextPaperSpaceName(blockRecords, blocks);
      blockRecords[recordName] = mintBlockRecord(document, blockRecordSchema.parse({ name: recordName }), ctx);
    }
    let record = { ...blockRecords[recordName!]! };
    if (lacksOwnHandle(record.handle)) record = mintBlockRecord(document, record, ctx);
    let block = blocks[recordName!];
    if (block === undefined) {
      block = blockDefinitionSchema.parse({
        name: recordName,
        handle: ctx.mint(),
        ownerHandle: record.handle,
        layer: "0",
        flags: 0,
        basePoint: { x: 0, y: 0, z: 0 },
        entities: [],
        endblkHandle: ctx.mint(),
      });
    } else if (lacksOwnHandle(block.handle) || lacksOwnHandle(block.endblkHandle) || block.ownerHandle !== record.handle) {
      block = {
        ...block,
        handle: lacksOwnHandle(block.handle) ? ctx.mint() : block.handle,
        endblkHandle: lacksOwnHandle(block.endblkHandle) ? ctx.mint() : block.endblkHandle,
        ownerHandle: record.handle,
      };
    }
    blocks[recordName!] = block;
    const layout: Layout = { ...source };
    layout.handle = ctx.mint();
    layout.ownerBlockRecordHandle = record.handle!;
    if (dictHandle !== undefined) layout.dictionaryOwnerHandle = dictHandle;
    record.layoutHandle = layout.handle;
    blockRecords[recordName!] = record;
    boundRecordHandles.add(record.handle!.toLowerCase());
    layouts[layout.name] = layout;
    layoutsBySpaceId[space.id] = layout;
    ownerBySpaceId.set(space.id, record.handle);
    changed = true;
  }

  if (!changed) {
    return {
      blocks: document.blocks,
      blockRecords: document.blockRecords,
      layouts: document.layouts,
      layoutsBySpaceId: document.layoutsBySpaceId,
      rawObjectGroups: document.rawObjectGroups,
      ownerBySpaceId,
    };
  }
  // Register every tab under its (possibly fresh) handle in the ACAD_LAYOUT
  // dictionary — only when the dictionary is known (never fabricated).
  let rawObjectGroups = document.rawObjectGroups;
  if (dictHandle !== undefined && rawObjectGroups !== undefined) {
    rawObjectGroups = patchLayoutDictionaryEntries(
      rawObjectGroups,
      dictHandle,
      Object.values(layouts).map((l) => ({ name: l.name, handle: l.handle })),
    );
  }
  return { blocks, blockRecords, layouts, layoutsBySpaceId, rawObjectGroups, ownerBySpaceId };
}
